import { Button, Alert } from 'react-bootstrap';
import { connect } from 'react-redux';
import axiosInstance from '../general/axiosInstance';
import React, { useEffect, useState, useMemo } from 'react';
import { setIsLoading } from '../general/actions';
import { currencyFormater } from '../general/utils';
import { C, barColor } from '../general/colors';
import { Link } from 'react-router-dom';

/* ── Tarjeta KPI ─────────────────────────────────────────────────────────────── */
function KpiCard({ icon, color, title, value, sub, children, to }) {
    const inner = (
        <div style={{
            background: '#fff', border: '1px solid var(--abi-border)', borderRadius: 14,
            padding: '16px 18px', borderLeft: `4px solid ${color}`,
            boxShadow: '0 2px 8px rgba(15,23,42,0.04)',
            transition: 'box-shadow 0.18s, transform 0.18s',
            height: '100%', display: 'flex', flexDirection: 'column', gap: 8,
        }}
            onMouseEnter={e => { if (to) { e.currentTarget.style.boxShadow = '0 6px 20px rgba(15,23,42,0.10)'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(15,23,42,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--abi-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                        {title}
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--abi-text)', lineHeight: 1 }}>{value}</div>
                    {sub && <div style={{ fontSize: 12, color: 'var(--abi-muted)', marginTop: 4 }}>{sub}</div>}
                </div>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className={`fa ${icon}`} style={{ color, fontSize: 20 }} />
                </div>
            </div>
            {children}
        </div>
    );
    return to
        ? <Link to={to} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>{inner}</Link>
        : inner;
}

/* ── Mini barra ──────────────────────────────────────────────────────────────── */
function MiniBar({ pct, color, label, value }) {
    return (
        <div style={{ marginBottom: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                <span style={{ color: 'var(--abi-text)', fontWeight: 600 }}>{label}</span>
                <span style={{ color: 'var(--abi-muted)' }}>{value}</span>
            </div>
            <div style={{ height: 6, background: 'var(--abi-border)', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: color, borderRadius: 999, transition: 'width 0.4s ease' }} />
            </div>
        </div>
    );
}

const STATUS_COLORS = {
    1: C.warning,
    2: C.primary,
    3: C.success,
};
const COLOR_PALETTE = [C.warning, C.primary, C.success, C.accent, C.provider, C.danger];

/* ── Dashboard ejecutivo ─────────────────────────────────────────────────────── */
function ExecutiveDashboard({ setIsLoading }) {
    const [agreements,     setAgreements]     = useState([]);
    const [selAgreement,   setSelAgreement]   = useState('');
    const [funds,          setFunds]          = useState([]);
    const [orders,         setOrders]         = useState([]);
    const [pendingCount,   setPendingCount]   = useState(0);
    const [weekDeliveries, setWeekDeliveries] = useState([]);
    const [loaded,         setLoaded]         = useState(false);
    const [error,          setError]          = useState('');

    useEffect(() => { fetchAll(); }, [selAgreement]);

    const fetchAll = async () => {
        setIsLoading(true);
        setError('');
        try {
            // 1. Contratos activos de la organización
            const agrRes  = await axiosInstance.get('/agreements/current-organization');
            const allAgrs = (agrRes.data || []).filter(a => a.is_active);
            setAgreements(allAgrs);

            const targetAgrs = selAgreement
                ? allAgrs.filter(a => a.id === parseInt(selAgreement))
                : allAgrs;

            if (!targetAgrs.length) { setLoaded(true); return; }

            const today      = new Date();
            const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
            const monthEnd   = today.toISOString().slice(0, 10);

            // 2. Llamadas paralelas por contrato + globales
            const [fundsArr, ordersArr, pendingRes, weekRes] = await Promise.all([
                Promise.all(targetAgrs.map(a =>
                    axiosInstance.get('/funds/agreements/' + a.id)
                        .then(r => r.data || []).catch(() => [])
                )),
                Promise.all(targetAgrs.map(a =>
                    axiosInstance.get('/orders/filters/', {
                        params: { agreement_id: a.id, created_start_date: monthStart, created_end_date: monthEnd },
                    }).then(r => r.data || []).catch(() => [])
                )),
                axiosInstance.get('/orders/pending-count').catch(() => ({ data: 0 })),
                axiosInstance.get('/orders/next-delivery/7').catch(() => ({ data: [] })),
            ]);

            setFunds(fundsArr.flat());
            setOrders(ordersArr.flat());
            const pd = pendingRes.data;
            setPendingCount(typeof pd === 'number' ? pd : pd?.count ?? (Array.isArray(pd) ? pd.length : 0));
            setWeekDeliveries(weekRes.data || []);
            setLoaded(true);
        } catch (e) {
            setError('Error cargando el dashboard. Intenta de nuevo.');
            console.log(e);
        } finally { setIsLoading(false); }
    };

    /* ── KPIs calculados ── */
    const kpi = useMemo(() => {
        const totalBudget  = funds.reduce((s, f) => s + parseFloat(f.amount  || 0), 0);
        const totalSpent   = funds.reduce((s, f) => s + parseFloat(f.spent   || 0), 0);
        const totalAvail   = funds.reduce((s, f) => s + parseFloat(f.available || 0), 0);
        const budgetPct    = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;
        const fundsAtRisk  = funds.filter(f => f.amount > 0 && (parseFloat(f.spent) / parseFloat(f.amount)) >= 0.8);
        const weekTotal    = weekDeliveries.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0);
        const byStatus   = {};
        const statusMeta = {};
        orders.forEach(o => {
            const st = o.order_status;
            if (st?.id) {
                byStatus[st.id] = (byStatus[st.id] || 0) + 1;
                if (!statusMeta[st.id]) statusMeta[st.id] = st;
            }
        });
        const maxStatus = Math.max(...Object.values(byStatus), 1);
        return { totalBudget, totalSpent, totalAvail, budgetPct, fundsAtRisk, weekTotal, byStatus, statusMeta, maxStatus };
    }, [funds, orders, weekDeliveries]);

    /* ── Top fondos por consumo ── */
    const topFunds = useMemo(() =>
        [...funds]
            .filter(f => parseFloat(f.amount) > 0)
            .map(f => ({
                ...f,
                pct: Math.min(100, (parseFloat(f.spent) / parseFloat(f.amount)) * 100),
            }))
            .sort((a, b) => b.pct - a.pct)
            .slice(0, 6),
        [funds]
    );

    const currMonth = new Date().toLocaleString('es-CO', { month: 'long', year: 'numeric' });

    return (
        <>
            {/* ── Ayuda ── */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, background: 'rgba(37,99,235,0.05)', border: '1px solid rgba(37,99,235,0.15)', borderRadius: 12, padding: '12px 16px', marginBottom: 16 }}>
                <i className="fa fa-lightbulb-o" style={{ color: 'var(--abi-primary)', fontSize: 18, marginTop: 2, flexShrink: 0 }} />
                <div style={{ fontSize: 13, color: 'var(--abi-text)', lineHeight: 1.65 }}>
                    <strong>Dashboard Ejecutivo:</strong> resumen en tiempo real del estado de la organización —
                    pedidos del mes actual por estado, ejecución presupuestal, fondos críticos,
                    aprobaciones pendientes y valor de entregas de los próximos 7 días.
                    Filtra por contrato o deja en "Todos" para una vista global.
                </div>
            </div>

            {/* ── Toolbar ── */}
            <div className="order-section" style={{ marginBottom: 16 }}>
                <div className="order-section-header" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <span><i className="fa fa-tachometer" /> Dashboard Ejecutivo — {currMonth}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                        <span style={{ fontSize: 12, color: 'var(--abi-muted)' }}>Contrato:</span>
                        <select
                            value={selAgreement}
                            onChange={e => setSelAgreement(e.target.value)}
                            style={{ fontSize: 12, border: '1px solid var(--abi-border)', borderRadius: 8, padding: '4px 8px', background: '#fff', cursor: 'pointer' }}
                        >
                            <option value="">Todos los contratos</option>
                            {agreements.map(a => (
                                <option key={a.id} value={a.id}>{a.ext_id} — {a.description}</option>
                            ))}
                        </select>
                        <button
                            onClick={fetchAll}
                            style={{ background: 'none', border: '1px solid var(--abi-border)', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: 'var(--abi-muted)' }}
                            title="Actualizar"
                        >
                            <i className="fa fa-refresh" />
                        </button>
                    </div>
                </div>
            </div>

            {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

            {loaded && agreements.length === 0 && (
                <div style={{ textAlign: 'center', padding: '56px 24px', background: '#fff', border: '1px solid var(--abi-border)', borderRadius: 14, boxShadow: '0 2px 8px rgba(15,23,42,0.04)' }}>
                    <i className="fa fa-file-text-o" style={{ fontSize: 44, color: 'var(--abi-border)', marginBottom: 16, display: 'block' }} />
                    <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--abi-text)', marginBottom: 8 }}>
                        No hay datos suficientes para mostrar el dashboard
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--abi-muted)', maxWidth: 420, margin: '0 auto', lineHeight: 1.7 }}>
                        El dashboard ejecutivo requiere al menos un <strong>contrato activo</strong> en la organización.
                        Activa un contrato existente o crea uno nuevo para comenzar a ver métricas.
                    </div>
                    <Link to="/config/organization" style={{ display: 'inline-block', marginTop: 18, fontSize: 13, color: 'var(--abi-primary)', fontWeight: 700, textDecoration: 'none', border: '1px solid var(--abi-primary)', borderRadius: 8, padding: '7px 16px' }}>
                        <i className="fa fa-arrow-right" style={{ marginRight: 6 }} />
                        Ir a configuración
                    </Link>
                </div>
            )}

            {loaded && agreements.length > 0 && (
                <>
                    {/* ── Fila 1: KPI Cards ── */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>

                        {/* Pedidos del mes */}
                        <KpiCard
                            icon="fa-shopping-basket" color={C.primary}
                            title="Pedidos este mes"
                            value={orders.length}
                            sub={`${Object.values(kpi.byStatus).reduce((s, v) => s + v, 0)} con estado registrado`}
                            to="/report/orders"
                        >
                            <div style={{ marginTop: 4 }}>
                                {Object.keys(kpi.byStatus).sort((a, b) => a - b).map((sid, i) => {
                                    const color = STATUS_COLORS[sid] || COLOR_PALETTE[i % COLOR_PALETTE.length];
                                    const label = kpi.statusMeta[sid]?.description || `Estado ${sid}`;
                                    return (
                                        <div key={sid} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                                            <div style={{ flex: 1, height: 4, background: 'var(--abi-border)', borderRadius: 999, overflow: 'hidden' }}>
                                                <div style={{ width: `${(kpi.byStatus[sid] / kpi.maxStatus) * 100}%`, height: '100%', background: color, borderRadius: 999 }} />
                                            </div>
                                            <span style={{ fontSize: 11, fontWeight: 700, minWidth: 20, textAlign: 'right' }}>{kpi.byStatus[sid]}</span>
                                            <span style={{ fontSize: 11, color: 'var(--abi-muted)', minWidth: 72 }}>{label}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </KpiCard>

                        {/* Pendientes de aprobación */}
                        <KpiCard
                            icon="fa-clock-o"
                            color={pendingCount > 0 ? C.warning : C.success}
                            title="Pend. aprobación"
                            value={pendingCount}
                            sub={pendingCount > 0 ? 'Requieren acción inmediata' : 'Sin pendientes ✓'}
                            to={pendingCount > 0 ? '/order/approve' : undefined}
                        >
                            {pendingCount > 0 && (
                                <div style={{ marginTop: 4 }}>
                                    <Link to="/order/approve" style={{ fontSize: 12, color: C.warning, fontWeight: 700, textDecoration: 'none' }}>
                                        Ir a aprobar <i className="fa fa-arrow-right" style={{ marginLeft: 4 }} />
                                    </Link>
                                </div>
                            )}
                        </KpiCard>

                        {/* Presupuesto */}
                        <KpiCard
                            icon="fa-money" color={C.accent}
                            title="Ejecución presupuestal"
                            value={`${kpi.budgetPct.toFixed(1)}%`}
                            sub={`${currencyFormater(kpi.totalSpent)} de ${currencyFormater(kpi.totalBudget)}`}
                            to="/report/funds"
                        >
                            <div>
                                <div style={{ height: 6, background: 'var(--abi-border)', borderRadius: 999, overflow: 'hidden', marginTop: 4 }}>
                                    <div style={{ width: `${Math.min(100, kpi.budgetPct)}%`, height: '100%', background: barColor(kpi.budgetPct), borderRadius: 999, transition: 'width 0.4s ease' }} />
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--abi-muted)', marginTop: 3 }}>
                                    Disponible: <strong style={{ color: kpi.totalAvail < 0 ? C.danger : C.success }}>{currencyFormater(kpi.totalAvail)}</strong>
                                </div>
                            </div>
                        </KpiCard>

                        {/* Fondos en riesgo */}
                        <KpiCard
                            icon="fa-exclamation-triangle"
                            color={kpi.fundsAtRisk.length > 0 ? C.danger : C.success}
                            title="Fondos en riesgo"
                            value={kpi.fundsAtRisk.length}
                            sub={kpi.fundsAtRisk.length > 0 ? 'Con >80% consumido' : 'Todos en nivel seguro'}
                            to="/report/funds"
                        >
                            {kpi.fundsAtRisk.length > 0 && (
                                <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    {kpi.fundsAtRisk.slice(0, 2).map(f => {
                                        const pct = (parseFloat(f.spent) / parseFloat(f.amount)) * 100;
                                        return (
                                            <div key={f.id} style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', color: C.danger, fontWeight: 600 }}>
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>{f.name}</span>
                                                <span>{pct.toFixed(0)}%</span>
                                            </div>
                                        );
                                    })}
                                    {kpi.fundsAtRisk.length > 2 && (
                                        <div style={{ fontSize: 11, color: 'var(--abi-muted)' }}>+{kpi.fundsAtRisk.length - 2} más…</div>
                                    )}
                                </div>
                            )}
                        </KpiCard>

                        {/* Entregas esta semana */}
                        <KpiCard
                            icon="fa-truck" color={C.provider}
                            title="Entregas próx. 7 días"
                            value={weekDeliveries.length}
                            sub={weekDeliveries.length > 0 ? currencyFormater(kpi.weekTotal) : 'Sin entregas programadas'}
                        >
                            {weekDeliveries.length > 0 && (
                                <div style={{ marginTop: 4, fontSize: 11, color: 'var(--abi-muted)' }}>
                                    Próxima: <strong style={{ color: 'var(--abi-text)' }}>
                                        {weekDeliveries[0]?.delivery_date} {weekDeliveries[0]?.delivery_time}
                                    </strong>
                                </div>
                            )}
                        </KpiCard>
                    </div>

                    {/* ── Fila 2: Fondos + Pedidos por estado ── */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12, marginBottom: 16 }}>

                        {/* Fondos por consumo */}
                        <div className="order-section">
                            <div className="order-section-header">
                                <i className="fa fa-bar-chart" /> Ejecución por fondo
                            </div>
                            <div className="order-section-body">
                                {topFunds.length === 0 && (
                                    <div style={{ fontSize: 13, color: 'var(--abi-muted)', textAlign: 'center', padding: '20px 0' }}>Sin fondos</div>
                                )}
                                {topFunds.map(f => (
                                    <MiniBar
                                        key={f.id}
                                        label={f.name}
                                        pct={f.pct}
                                        color={barColor(f.pct)}
                                        value={`${f.pct.toFixed(0)}% — ${currencyFormater(parseFloat(f.available))}`}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Pedidos por estado */}
                        <div className="order-section">
                            <div className="order-section-header">
                                <i className="fa fa-tasks" /> Pedidos del mes por estado
                            </div>
                            <div className="order-section-body">
                                {orders.length === 0 && (
                                    <div style={{ fontSize: 13, color: 'var(--abi-muted)', textAlign: 'center', padding: '20px 0' }}>Sin pedidos este mes</div>
                                )}
                                {Object.keys(kpi.byStatus).sort((a, b) => a - b).map((sid, i) => {
                                    const count = kpi.byStatus[sid] || 0;
                                    const pct   = orders.length > 0 ? (count / orders.length) * 100 : 0;
                                    const color = STATUS_COLORS[sid] || COLOR_PALETTE[i % COLOR_PALETTE.length];
                                    const label = kpi.statusMeta[sid]?.description || `Estado ${sid}`;
                                    return (
                                        <div key={sid} style={{ marginBottom: 14 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                                <span style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7 }}>
                                                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                                                    {label}
                                                </span>
                                                <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--abi-text)' }}>{count}</span>
                                            </div>
                                            <div style={{ height: 8, background: 'var(--abi-border)', borderRadius: 999, overflow: 'hidden' }}>
                                                <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 999, transition: 'width 0.4s ease' }} />
                                            </div>
                                            <div style={{ fontSize: 11, color: 'var(--abi-muted)', marginTop: 2 }}>{pct.toFixed(0)}% del total del mes</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* ── Fila 3: Entregas próximas ── */}
                    {weekDeliveries.length > 0 && (
                        <div className="order-section">
                            <div className="order-section-header" style={{ justifyContent: 'space-between' }}>
                                <span><i className="fa fa-truck" /> Entregas próximos 7 días</span>
                                <span style={{ fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--abi-muted)' }}>
                                    {weekDeliveries.length} pedido{weekDeliveries.length !== 1 ? 's' : ''} · {currencyFormater(kpi.weekTotal)}
                                </span>
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                                <table className="report-table mb-0" style={{ width: '100%', minWidth: 560 }}>
                                    <thead>
                                        <tr>
                                            <th>Pedido</th>
                                            <th>Estado</th>
                                            <th className="d-none d-md-table-cell">Proveedor / Cliente</th>
                                            <th>Fecha entrega</th>
                                            <th className="d-none d-sm-table-cell">Dirección</th>
                                            <th className="text-end">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {weekDeliveries.map(o => {
                                            const s = { 1: { bg: '#fef9c3', color: '#854d0e' }, 2: { bg: '#dbeafe', color: '#1e40af' }, 3: { bg: '#dcfce7', color: '#166534' } }[o.order_status?.id] || { bg: '#f1f5f9', color: '#475569' };
                                            return (
                                                <tr key={'wd-' + o.id}>
                                                    <td className="report-order-id">
                                                        <Link to={'/order/search/' + o.id} style={{ color: 'var(--abi-primary)', fontWeight: 800, textDecoration: 'none' }}>#{o.id}</Link>
                                                    </td>
                                                    <td>
                                                        <span className="report-status-badge" style={{ background: s.bg, color: s.color, border: `1px solid ${s.color}44` }}>
                                                            {o.order_status?.description}
                                                        </span>
                                                    </td>
                                                    <td className="d-none d-md-table-cell" style={{ fontSize: 12 }}>
                                                        {o.agreement?.provider?.name || o.agreement?.customer?.name || '—'}
                                                    </td>
                                                    <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                                                        <strong>{o.delivery_date}</strong>
                                                        <div style={{ fontSize: 11, color: 'var(--abi-muted)' }}>{o.delivery_time}</div>
                                                    </td>
                                                    <td className="d-none d-sm-table-cell" style={{ fontSize: 12, color: 'var(--abi-muted)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {o.delivery_address || '—'}
                                                    </td>
                                                    <td className="text-end" style={{ fontWeight: 700 }}>{currencyFormater(o.total_amount)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr className="report-tfoot">
                                            <th colSpan={5} className="text-end">Total entregas semana</th>
                                            <th className="text-end">{currencyFormater(kpi.weekTotal)}</th>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
}

function mapStateToProps(state) { return { isLoading: state.isLoading, roles: state.roles }; }
const mapDispatchToProps = { setIsLoading };
export default connect(mapStateToProps, mapDispatchToProps)(ExecutiveDashboard);
